import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowCounterClockwise,
  CheckCircle,
  ClipboardText,
  Warning,
  X,
} from "@phosphor-icons/react";
import {
  KIND_BY_TYPE,
  dismissNotificationAlerts,
  fetchNotificationAlerts,
} from "../../slice/AlertsSlice";
import { appendAIPaneTo } from "../../utils/aiPaneUrl";
import {
  AlertActions,
  AlertBannerStack,
  AlertBody,
  AlertCard,
  AlertDismissButton,
  AlertIconSlot,
  AlertLinkButton,
  AlertMeta,
  AlertRollup,
  AlertTitle,
} from "./AlertBannerStyled";

/**
 * Login/real-time alert banner for the loud notification types.
 *
 * Why this exists: an approval used to be discoverable only by
 * navigating to the "Shared for Review" tab, which is not the default
 * tab on Home, so a reviewer's decision could sit unseen indefinitely.
 *
 * Why it is capped: the backend restricts this feed to approvals,
 * rework requests, assignments and urgent tasks, and returns at most
 * three cards plus a count of the rest. A partner with a backlog gets
 * three real cards and a "+N more" link rather than a screen-filling
 * wall, and no volume of fax/routing/reminder traffic can push an
 * approval out of view.
 *
 * Dismissal is intentionally not "mark read": the backend stamps a
 * separate `alert_dismissed_at`, so clearing the banner leaves the
 * notification bell's own unread state alone (Matthew, 2026-07-26).
 */

// Stable identities so the defensive fallbacks below cannot cause a
// re-render loop by handing useSelector a fresh object each time.
const EMPTY_RESULTS = [];
const EMPTY_COUNTS = {
  urgent_task: 0,
  approved: 0,
  rework_requested: 0,
  assigned: 0,
};

const KIND_ICON = {
  urgent_task: Warning,
  approved: CheckCircle,
  rework_requested: ArrowCounterClockwise,
  assigned: ClipboardText,
};

const KIND_TITLE = {
  urgent_task: "Urgent task",
  approved: "Approved",
  rework_requested: "Sent back for rework",
  assigned: "New assignment",
};

// An assignment completing shares the "assigned" kind for icon and
// ordering purposes but is emphatically not a new assignment, so it
// carries its own title rather than being mistitled.
const TYPE_TITLE_OVERRIDE = {
  ASSIGNMENT_COMPLETED: "Assignment complete",
};

// Singular/plural phrasing for the roll-up line, so it reads as a
// sentence rather than a stat block.
const KIND_SUMMARY = {
  urgent_task: (n) => (n === 1 ? "1 urgent task" : `${n} urgent tasks`),
  approved: (n) => `${n} approved`,
  rework_requested: (n) => `${n} sent back for rework`,
  assigned: (n) => (n === 1 ? "1 new assignment" : `${n} new assignments`),
};

export const buildRollupSummary = (countsByKind, shownKinds) => {
  const parts = [];
  // Urgent first -- the same rank order the backend's allowlist uses,
  // because this is the one the user chose by hand rather than the
  // system inferring it.
  ["urgent_task", "rework_requested", "assigned", "approved"].forEach(
    (kind) => {
      const total = countsByKind?.[kind] || 0;
      const remaining = total - (shownKinds?.[kind] || 0);
      if (remaining > 0) {
        parts.push(KIND_SUMMARY[kind](remaining));
      }
    }
  );
  return parts.join(", ");
};

const AlertBanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Tolerate a missing slice. This renders on every authenticated page,
  // so a bad read here would take down the whole app shell rather than
  // just the banner.
  const results = useSelector((state) => state.alerts?.results) || EMPTY_RESULTS;
  const remainingCount = useSelector((state) => state.alerts?.remainingCount) || 0;
  const countsByKind = useSelector((state) => state.alerts?.countsByKind) || EMPTY_COUNTS;
  // Gate on the loaded user, not auth.isAuthenticated: that flag is
  // initialized false and only ever written on logout, so keying off it
  // would mean the fetch never fires. Layout dispatches getCurrentUser,
  // so this populates shortly after mount and again after a login,
  // which is exactly when the backlog should load.
  const userId = useSelector((state) => state.auth?.user?.id);

  useEffect(() => {
    // This is what covers "approved while I was logged out": anything
    // still undismissed is waiting here at next login.
    if (userId) {
      dispatch(fetchNotificationAlerts());
    }
  }, [dispatch, userId]);

  const shownKinds = useMemo(() => {
    const counts = {};
    results.forEach((row) => {
      const kind = row.kind || KIND_BY_TYPE[row.notification_type];
      if (kind) counts[kind] = (counts[kind] || 0) + 1;
    });
    return counts;
  }, [results]);

  const handleDismiss = useCallback(
    (id) => {
      dispatch(dismissNotificationAlerts({ notification_ids: [id] }));
    },
    [dispatch]
  );

  const handleDismissAll = useCallback(() => {
    dispatch(dismissNotificationAlerts({ all: true }));
  }, [dispatch]);

  const handleOpen = useCallback(
    (alert) => {
      // Every kind deep-links to the document it concerns. An
      // Assignments tab does not exist yet, so routing assignments to
      // "/home?tab=assignments" would silently dump the user on the
      // default Home view; the document is the useful destination
      // either way.
      if (alert.matter_id && alert.document) {
        navigate(
          appendAIPaneTo(
            `/matter/${alert.matter_id}/D${alert.document}`,
            location.search
          )
        );
        return;
      }
      navigate("/home");
    },
    [navigate, location.search]
  );

  if (!results.length) return null;

  const rollupSummary = buildRollupSummary(countsByKind, shownKinds);

  return (
    <AlertBannerStack role="region" aria-label="Recent activity alerts">
      {results.map((alert) => {
        const kind = alert.kind || KIND_BY_TYPE[alert.notification_type];
        const Icon = KIND_ICON[kind] || CheckCircle;
        const actor = alert.action_user_name;
        const docName = alert.document_name;
        const title =
          TYPE_TITLE_OVERRIDE[alert.notification_type] ||
          KIND_TITLE[kind] ||
          "Update";

        return (
          <AlertCard key={alert.id} $tone={kind} role="alert">
            <AlertIconSlot $tone={kind}>
              <Icon size={20} weight="fill" aria-hidden="true" />
            </AlertIconSlot>
            <AlertBody>
              <AlertTitle>{title}</AlertTitle>
              <AlertMeta title={alert.message}>
                {docName
                  ? `${docName}${actor ? ` — ${actor}` : ""}`
                  : alert.message}
              </AlertMeta>
            </AlertBody>
            <AlertActions>
              <AlertLinkButton type="button" onClick={() => handleOpen(alert)}>
                Open
              </AlertLinkButton>
              <AlertDismissButton
                type="button"
                onClick={() => handleDismiss(alert.id)}
                aria-label={`Dismiss alert: ${title}${
                  docName ? ` for ${docName}` : ""
                }`}
              >
                <X size={16} weight="bold" aria-hidden="true" />
              </AlertDismissButton>
            </AlertActions>
          </AlertCard>
        );
      })}

      {remainingCount > 0 && (
        <AlertRollup>
          <span>
            {rollupSummary
              ? `+${remainingCount} more — ${rollupSummary}`
              : `+${remainingCount} more`}
          </span>
          <AlertActions>
            <AlertLinkButton type="button" onClick={() => navigate("/home")}>
              View all
            </AlertLinkButton>
            <AlertLinkButton type="button" onClick={handleDismissAll}>
              Dismiss all
            </AlertLinkButton>
          </AlertActions>
        </AlertRollup>
      )}
    </AlertBannerStack>
  );
};

export default AlertBanner;
